/**
 * <navera-buy-now-slot>: holds the product page's Buy Now slot while Razorpay Magic Checkout
 * swaps in its own button, so shoppers never see Shopify's "Buy it now" turn into Razorpay's.
 *
 * What Razorpay does (magic-shopify.js, loaded by snippets/razorpay-magic-checkout.liquid):
 * it adds .magic-hide to Shopify's .shopify-payment-button and inserts
 * <div class="magic-buy-btn__parent"><div class="magic-buy-btn"><magic-checkout-btn></div></div>
 * right after it. <magic-checkout-btn> draws its button in a closed shadow root.
 *
 * What this does (styles in snippets/buy-buttons-styles.liquid):
 * - data-state="pending": Shopify's button is invisible from first paint; a placeholder holds the slot.
 * - data-state="ready": Razorpay's button has rendered (non-zero height), so it fades in.
 * - data-state="fallback": nothing from Razorpay after FALLBACK_MS (script blocked or failed),
 *   so Shopify's own Buy it now is shown and the customer can still check out.
 *   If Razorpay's button turns up later, the slot still switches to "ready".
 */
const FALLBACK_MS = 5000;

class NaveraBuyNowSlot extends HTMLElement {
  /** @type {Element | null} */
  #button = null;
  #timer = 0;
  #resizeObserver = new ResizeObserver(() => this.#check());
  #mutationObserver = new MutationObserver(() => this.#watch());

  connectedCallback() {
    this.#mutationObserver.observe(this, { childList: true, subtree: true });
    this.#watch();

    if (this.dataset.state === 'pending') {
      window.clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => {
        if (this.dataset.state === 'pending') this.dataset.state = 'fallback';
      }, FALLBACK_MS);
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#button = null;
  }

  /** Starts measuring Razorpay's button as soon as it is inserted into the slot. */
  #watch() {
    const button = this.querySelector('magic-checkout-btn');
    if (button && button !== this.#button) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver.observe(button);
      this.#button = button;
    }
    this.#check();
  }

  #check() {
    if (!this.#button?.isConnected) return;
    if (this.#button.getBoundingClientRect().height > 0 && this.dataset.state !== 'ready') {
      window.clearTimeout(this.#timer);
      this.dataset.state = 'ready';
    }
  }
}

if (!customElements.get('navera-buy-now-slot')) {
  customElements.define('navera-buy-now-slot', NaveraBuyNowSlot);
}
